import React, { useState, useContext } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Avatar,
  Divider,
  Alert,
  Snackbar,
  Chip,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { AuthContext } from '../App';
import { useMutation } from '@tanstack/react-query';
import { authService } from '../services/auth';

const Profile: React.FC = () => {
  const auth = useContext(AuthContext);
  const user = auth?.user;

  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [isEditing, setIsEditing] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [touchedEmail, setTouchedEmail] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Email validation function (same as Signup page)
  const validateEmail = (value: string): string | null => {
    if (!value.trim()) return 'Email is required.';
    const normalizedEmail = value.trim().toLowerCase();
    if (normalizedEmail.length > 254) return 'Email must not exceed 254 characters.';
    if (normalizedEmail.split('@').length - 1 !== 1) return 'Email must contain a single "@" symbol.';
    const [localPart, domainPart] = normalizedEmail.split('@');
    if (!localPart || !domainPart) return 'Email must have both local and domain parts.';
    if (localPart.length > 64) return 'Email local part must not exceed 64 characters.';
    if (!/^[a-z0-9._%+-]+$/.test(localPart)) {
      return "Email local part contains invalid characters. Allowed: letters, numbers, '.', '_', '%', '+', '-'.";
    }
    if ('._%+-'.includes(localPart[0]) || '._%+-'.includes(localPart[localPart.length - 1])) {
      return "Email local part cannot start or end with '.', '_', '%', '+' or '-'.";
    }
    if (normalizedEmail.includes('..')) return 'Email cannot contain consecutive dots.';
    if (!domainPart.includes('.')) return 'Email domain must contain at least one dot (e.g., example.com).';
    return null;
  };

  const handleEmailBlur = () => {
    setTouchedEmail(true);
    const error = validateEmail(email);
    setEmailError(error);
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    // Clear error when user starts typing
    if (emailError && touchedEmail) {
      setEmailError(null);
    }
  };

  const updateMutation = useMutation({
    mutationFn: async () => {
      // Validate email before submission
      const emailValidationError = validateEmail(email);
      if (emailValidationError) {
        setTouchedEmail(true);
        setEmailError(emailValidationError);
        throw new Error(emailValidationError);
      }

      // Call backend to update profile
      const payload: {
        first_name?: string;
        last_name?: string;
        email?: string;
        phone?: string;
      } = {
        first_name: firstName,
        last_name: lastName,
        email,
        phone,
      };
      return await authService.updateProfile(payload);
    },
    onSuccess: (updatedUser) => {
      setSnackbar({
        open: true,
        message: 'Profile updated successfully!',
        severity: 'success',
      });
      setIsEditing(false);
      setEmailError(null);
      setTouchedEmail(false);
      if (auth?.setUser) {
        auth.setUser(updatedUser);
      }
    },
    onError: (error: any) => {
      // Handle field-specific errors from backend
      const backendErrors = error?.response?.data || {};
      
      // Check for email-specific error
      if (backendErrors.email) {
        const emailErr = Array.isArray(backendErrors.email) 
          ? backendErrors.email[0] 
          : backendErrors.email;
        setEmailError(emailErr);
        setTouchedEmail(true);
      }
      
      const message =
        backendErrors.detail ||
        backendErrors.message ||
        error?.message ||
        'Failed to update profile';
      setSnackbar({
        open: true,
        message,
        severity: 'error',
      });
    },
  });

  const handleSave = () => {
    updateMutation.mutate();
  };

  const handleCancel = () => {
    setFirstName(user?.first_name || '');
    setLastName(user?.last_name || '');
    setEmail(user?.email || '');
    setPhone(user?.phone || '');
    setIsEditing(false);
    setEmailError(null);
    setTouchedEmail(false);
  };

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          User not found
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Hero Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #00f2fe 100%)',
          borderRadius: 4,
          p: 6,
          mb: 5,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 3,
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid rgba(255,255,255,0.3)',
              }}
            >
              <PersonIcon sx={{ color: 'white', fontSize: 48 }} />
            </Box>
            <Box>
              <Typography variant="h3" component="h1" fontWeight={900} sx={{ color: 'white', mb: 1 }}>
                Profile Settings
              </Typography>
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 400 }}>
                Manage your account information and preferences
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={4}>
        {/* Profile Card */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
              border: '1px solid rgba(102, 126, 234, 0.1)',
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #00f2fe 100%)',
              },
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  mx: 'auto',
                  mb: 3,
                  bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  fontSize: 48,
                  fontWeight: 700,
                  border: '4px solid white',
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                }}
              >
                {user.first_name?.[0] || user.username[0].toUpperCase()}
              </Avatar>
              <Typography variant="h4" fontWeight={800} gutterBottom>
                {user.first_name} {user.last_name}
              </Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                @{user.username}
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                <Chip
                  label={user.is_staff ? 'Administrator' : 'User'}
                  color={user.is_staff ? 'primary' : 'default'}
                  sx={{
                    fontWeight: 700,
                    bgcolor: user.is_staff ? 'primary.main' : undefined,
                    color: user.is_staff ? 'white' : undefined,
                  }}
                />
                {user.is_premium && (
                  <Chip
                    label="Premium"
                    sx={{
                      bgcolor: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                      background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                      color: 'white',
                      fontWeight: 700,
                    }}
                  />
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Personal Information */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
              border: '1px solid rgba(102, 126, 234, 0.1)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PersonIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Typography variant="h5" fontWeight={800}>
                    Personal Information
                  </Typography>
                </Box>
                {!isEditing && (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setIsEditing(true)}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Edit Profile
                  </Button>
                )}
              </Box>

              <Divider sx={{ mb: 4 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={!isEditing}
                    InputProps={{
                      startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={!isEditing}
                    InputProps={{
                      startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    onBlur={handleEmailBlur}
                    disabled={!isEditing}
                    required
                    error={Boolean(emailError && touchedEmail)}
                    helperText={touchedEmail ? emailError : 'Email is validated using strict format checks, Gmail normalization, and domain verification.'}
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone"
                    type="tel"
                    value={phone || ''}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={!isEditing}
                    InputProps={{
                      startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Username"
                    value={user.username}
                    disabled
                    helperText="Username cannot be changed"
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
              </Grid>

              {isEditing && (
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 4,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={updateMutation.isPending}
                    startIcon={updateMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 700,
                      px: 4,
                      boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                        boxShadow: '0 12px 32px rgba(102, 126, 234, 0.5)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Account Settings */}
          <Card
            sx={{
              mt: 4,
              borderRadius: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
              border: '1px solid rgba(102, 126, 234, 0.1)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #f093fb 0%, #4facfe 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <SecurityIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Typography variant="h5" fontWeight={800}>
                  Account Settings
                </Typography>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Stack spacing={2}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'rgba(102, 126, 234, 0.2)',
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                  }}
                >
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    Security
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Manage your password and security settings
                  </Typography>
                  <Button
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Change Password
                  </Button>
                </Box>

                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'rgba(102, 126, 234, 0.2)',
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                  }}
                >
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    Notifications
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Configure how you receive notifications
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<NotificationsIcon />}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Manage Notifications
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Profile;
